import { FeatureSupportService } from './../../services/feature-support.service';
import { BackupService } from './../../services/backup.service';
import { ChangeDetectorRef, Component, ElementRef, HostBinding, OnInit, Type, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Device, Logger, FlasherObserver, sleep } from 'bossa-web';
import { FirmwareInfo } from 'src/app/model/firmware-info';
import { FirmwareService } from 'src/app/services/firmware.service';
import { ConnectionService } from 'src/app/services/connection.service';
import { Backup } from 'src/app/model/backup';
import { buf } from 'crc-32';
import { ConfirmationDialogService } from 'src/app/views/confirmation-dialog/confirmation-dialog.service';
import { InstrumentComponentBase } from 'src/app/helpers/instrument-component-base';
import { InstrumentConnection, InstrumentConnectionMode, InstrumentInterface } from 'src/app/model/instrument-connection';
import { DeviceRegistryService } from 'src/app/services/device-registry.service';
import { EMEO_SAX_V1, HardwareInfo, HardwareTypes, NO_DEVICE } from 'src/app/model/device-info';
import { rootViews } from 'src/app/helpers/log-config';
import { InstrumentViewComponentBaseComponent } from 'src/app/helpers/instrument-view-component-base.component';
import { INSTRUMENT_INFOS_EX } from 'src/app/model/instruments/device-definitions';
import { InstrumentInfoEx } from 'src/app/model/instrument-info-ex';
import { DreamRPC, RPCRange, RPCRangeInfo, RPCRangeType } from 'src/app/helpers/dream-rpc';

const log = rootViews.getChildCategory("flasher");

export type CloseCallback = () => Promise<void>;

enum FlasherSteps {
  Welcome = 0,
  DeviceSelection = 1,
  RecoveryDeviceConnection = 2,
  FirmwareSelection = 3,
  ConfigurationBackup = 4,
  DeviceConnection = 5,
  RpcActivation = 6,
  SoundbankFlash = 7,
  DeviceReconnect = 8,
  ConfigurationRestore = 9,
  Completion = 10
}

interface DeviceSelectionInfo {

  index: string;

  hardware: HardwareInfo;

  name: string; 

  description: string;

  component: Type<InstrumentViewComponentBaseComponent>;
}

@Component({
  selector: 'app-soundbank-flasher',
  templateUrl: './soundbank-flasher.component.html',
  styleUrls: ['./soundbank-flasher.component.scss'],
})
export class SoundbankFlasherComponent extends InstrumentComponentBase implements OnInit {

  @ViewChild("nav") // Get a reference to the ngbNav
  nav! : any;

  @HostBinding('class.is-fullscreen') isFullscreen = false;

  closeCallback: CloseCallback | undefined;

  protected serialPort: SerialPort | undefined;

  protected device: Device | undefined;

  protected logger: Logger;
  protected observer: FlasherObserver;

  protected actionProgress: string = "0%";

  private circuitId?: string;

  get hasCircuitId() : boolean {
    return (this.circuitId != undefined);
  }
  
  steps: number[] = [
    FlasherSteps.Welcome,
    FlasherSteps.DeviceSelection,
    FlasherSteps.FirmwareSelection,
//    FlasherSteps.DeviceConnection,
    FlasherSteps.RpcActivation,
    FlasherSteps.SoundbankFlash,
    FlasherSteps.DeviceReconnect,
    FlasherSteps.Completion
  ];

  progress: any = {};

  get isActive() : boolean {

    if (this.progress) {

      for (var key of Object.keys(this.progress)) {
        if (this.progress[key] === 'active') {
          return true;
        }
      }
    }

    return false;
  }

  private _availableFirmwares: FirmwareInfo[] = [];

  public set availableFirmwares(value: FirmwareInfo[]) {
    this._availableFirmwares = value;

    this.populateFirmwares();
  }

  private populateFirmwares() {
    let hw = this.deviceType;

    this.updateFirmwares = [];

    if (hw) {
      this.updateFirmwares = this._availableFirmwares.filter( fw => (fw.hardwareTypes.contains( hw.type ) && fw.hardwareVersions.contains( hw.version)));
    }
    
    if (this.updateFirmwares.length == 0) {
      this.updateFirmwares = this._availableFirmwares;
    }

    if (this.updateFirmwares.length == 1) {
      this.firmwareSelection.setValue( this.updateFirmwares[0].version );
    }
    else {
      this.firmwareSelection.setValue( this.updateFirmwares[0].version );
    }
  }

  updateFirmwares: FirmwareInfo[] = [];
  
  nextCaption: string = "Next";

  protected _canMoveToNextStep: boolean = false;

  public buildDescription(info : FirmwareInfo) : string {

    var summary = '';

    for (var type of info.hardwareTypes) {

      switch (type) {
        case HardwareTypes.SOUNDBANK_MS:
          if (info.title) {
            summary += info.title;
          }
          else {
            summary += 'Soundbank';
          } 

          summary += " - "
          break;

        default:
      }

      summary += info.version;

      switch (info.type) {
        case 'release':
          break;
  
        default:
          summary += ' ' + info.type;
      }
    }

    return summary;
  }

  set canMoveToNextStep(flag: boolean) {

    this._canMoveToNextStep = flag;

    if (flag) {
      this.nextCaption = "Continue";
    }

    this.cd.detectChanges();
  }

  get canMoveToNextStep() : boolean {
    return this._canMoveToNextStep;
  }

  protected _currentStep: number = 0;

  protected nextStep?: number;

  set currentStep(value: number) {
    this._currentStep = value; 

    this.canMoveToNextStep = false;
    this.nextStep = undefined;

    this.prepareStep(this._currentStep);
    this.nav?.select(this._currentStep);
  }

  get currentStep() : number {
    return this._currentStep;
  }

  protected isInBootloader: boolean = false;
  protected isConnected: boolean = false;
  
  firmwareSelection: UntypedFormControl;

  devices: DeviceSelectionInfo[] = [];

  selectedDevice?: DeviceSelectionInfo = undefined;

  deviceSelection: UntypedFormControl;

  constructor(
    public fs: FeatureSupportService,
    public deviceRegistry: DeviceRegistryService,
    public confirmService: ConfirmationDialogService,
    private backupService: BackupService,
    public activeModal: NgbActiveModal,
    public cs: ConnectionService,
    private cd: ChangeDetectorRef,
    private firmwareLoadService: FirmwareService) { 

    super(cs);

    this.firmwareSelection = new UntypedFormControl('firmwareVersion');
    this.firmwareSelection.valueChanges.subscribe( (value:any) => {
        comp.firmwareSelected();
    });

    var usableDevices: InstrumentInfoEx[] = [];
    
    if (this.deviceType) {
      let hw = this.deviceType;

      usableDevices = INSTRUMENT_INFOS_EX.filter( (value) => { return value.devices.find( value => ((value.type == hw.type) && (value.version == hw.version))); })
    }

    if (usableDevices.length == 0) {
      usableDevices = INSTRUMENT_INFOS_EX.filter( (value) => { return !value.hidden; })
    }

    this.devices = usableDevices.map( (instrument, index) => {
      return { 
        index: 'device-' + index,
        name: instrument.name,
        description: instrument.description || '',
        hardware: instrument.hardware,
        component: instrument.instrumentComponent
      }
    })

    this.deviceSelection = new UntypedFormControl('deviceSelection');

    if (this.devices.length == 1) {
      this.deviceSelection.setValue(this.devices[0].index);
    }

    this.deviceSelection.valueChanges.subscribe( (value:any) => {
        comp.deviceSelected();
    });



    let comp = this;

    this.logger = <Logger>{

      withDebug: false,

      debug(message?: unknown, ...optionalParams: unknown[]): void {
        log.debug(String(message), optionalParams);
      },

      log(message?: unknown, ...optionalParams: unknown[]): void {
        log.info(String(message), optionalParams);
      },

      error(message?: unknown, ...optionalParams: unknown[]): void {
        log.error(String(message), optionalParams);
      }
    }

    this.observer = <FlasherObserver> {

      onStatus(message : string) : void {
        comp.write(message, []);
      },

      onProgress(num: number, div: number) : void {
        comp.actionProgress = String(Math.trunc(100 * num / div)) + '%';
        comp.cd.detectChanges();
      }

    }
  }

  deviceSelected() {
    let newValue = this.deviceSelection.value;
    log.debug("Selected device " + newValue);

    let selectedDevice = this.devices.find( device => device.index == newValue );

    this._selectedHardware = selectedDevice?.hardware || null;
    this.selectedDevice = selectedDevice;

    // this.firmwareName = newValue;
    this.cd.detectChanges();
  }

  firmwareSelected() {
    let newValue = this.firmwareSelection.value;
    if (newValue != "firmwareVersion") {
      log.debug("Selected firmware " + newValue);
      this.firmwareName = newValue;
      this.cd.detectChanges();
    }
  }

  protected rebooting : boolean = false;

  override async onInstrumentChanged(current: InstrumentConnection | undefined, before: InstrumentConnection | undefined) : Promise<void> {

    if (this.currentStep === FlasherSteps.DeviceReconnect) {

      if (!current && this.rebooting) {
        this.setProgress("rebootDevice", 'done');
        return;
      }

      if (current) {
        this.setProgress("connectDevice", 'done');
      }
      else {
        this.setProgress("connectDevice", 'error');
      }

      if (this.currentInstrument && this.circuitId) {
        this.setProgress('configDevice', 'active');

        let deviceInfo = await this.deviceRegistry.getDeviceInfoForCircuit(this.circuitId, Object.assign({}, this.deviceType));

        if (deviceInfo) {
          // deviceInfo = Object.assign({}, EMEO_DEVICE);
          this.currentInstrument.sendHardwareInfo(deviceInfo);
        }

        this.setProgress('configDevice', 'done');
      }
      else {
        this.setProgress('configDevice', 'done');
      }

      await this.refreshInstrumentInfo();
      this.canMoveToNextStep = true;

      if (this.autobackup) {
        this.currentStep = FlasherSteps.ConfigurationRestore;
      }
      else {
        this.currentStep = FlasherSteps.Completion;
      }
    }
  }

  protected write(message: unknown, optionalParams: unknown[]): void {
    log.debug(<string>message, ...optionalParams);
  }

  ngOnInit(): void {
  }

  autobackup?: Backup;

  allowSelection: boolean = true;

  async restoreBackup() : Promise<void> {

    if (this.currentInstrument) {
      
      if (this.autobackup) {
  
        this.setProgress('restore', 'active');
        this.allowSelection = false;
        this.actionProgress = '0%';

        this.logger.debug("Starting restore of global settings.")
        await this.backupService.restoreGlobalSettings(this.currentInstrument, this.autobackup);

        this.logger.debug("Starting restore of UNC tables.")
        await this.backupService.restoreUNC(this.currentInstrument, this.autobackup, { progress: (step: number, total: number) => {
          this.actionProgress = String(Math.trunc(100 * step / total)) + '%';
          this.cd.detectChanges();
        }});

        this.logger.debug("Refreshing instrument info the receive new settings.")
        await this.currentInstrument.refreshInstrumentInfo();

        this.actionProgress = '100%';
        this.setProgress('restore', 'done');
        this.canMoveToNextStep = true;
        this.currentStep = FlasherSteps.Completion;
      }
    }
  }

  /**
   * Create a backup of the connected EMEO
   */
  async performBackup() : Promise<void> {

    if (this.currentInstrument) {

      this.actionProgress = "0%";
      this.setProgress('backup', 'active');
      this.cd.detectChanges();

      let backup = await this.backupService.backupEverything(this.currentInstrument, { progress: (step: number, total: number) => {
        this.actionProgress = String(Math.trunc(100 * step / total)) + '%';
        this.cd.detectChanges();
      }});

      if (backup) {
        log.debug("Backup completed");

        backup.name = "Before firmware update";
        backup.automatic = true;
        this.backupService.addLocalBackup(backup);
        this.autobackup = backup;

        this.canMoveToNextStep = true;

        this.actionProgress = "100%";
        this.setProgress('backup', 'done');

        this.cd.detectChanges();
      }
    }
  }

  requiresBootloader: boolean = true;
  
  // This will allow to skip the flash screen and commence flashing
  directFlash: boolean = this.fs.hasFeature('Updater');

  prepare() {

    this.steps = [];

    this.steps.push( FlasherSteps.Welcome );

    if (this.devices.length > 1) {
      this.steps.push( FlasherSteps.DeviceSelection );
    }
    else {
    }

    this.steps.push( FlasherSteps.FirmwareSelection );
    this.steps.push( FlasherSteps.RpcActivation );
    this.steps.push( FlasherSteps.SoundbankFlash );

//    if (this.flasherType === FlasherType.SAMBA) {
//      this.steps.push( FlasherSteps.DeviceReconnect );
//    }

    this.steps.push( FlasherSteps.Completion );
    this.steps.sort( (a:number, b: number ) => {return a-b;});

    this.currentStep = this.steps[0] || FlasherSteps.Welcome;
    // this.currentStep = FlasherSteps.DeviceReconnect;
    this.actionProgress = '0%';

    if (this.updateFirmwares.length == 1) {
      this.firmwareSelection.setValue( this.updateFirmwares[0].version );
    }
    else {
      this.firmwareSelection.setValue( this.updateFirmwares[0].version );
    }

    this.nav?.select(this._currentStep);    
  }

  nextTab() {
    let newSteps = this.steps.filter( (step: number) => { return (step > this.currentStep); } );
    let nextStep = newSteps.sort((a: number, b: number) => { return b-a; } ).pop();

    if (nextStep) {
      this.currentStep = nextStep;
    }
  }

  skipBackup() {
    this.autobackup = undefined;
    
    this.nextTab();
  }

  private prepareStep(step: number) {

    this.progress = undefined;

    switch (step) {
      case FlasherSteps.Welcome:
        this.canMoveToNextStep = true;
        this.nextCaption = "Start";
        break;
      
      case FlasherSteps.FirmwareSelection:
        
        this.populateFirmwares();

        if (this.updateFirmwares.length == 1) {
          this.firmwareName = this.updateFirmwares[0].version;
        }
        else {
          this.firmwareName = undefined;
        }

        this.actionProgress = '0%';
        this.firmwareSelection.enable();
        this.setProgress('loadSoundbank', 'wait');
        this.setProgress('verifySoundbank', 'wait');

        if (this.directFlash && !(this.fs.developmentMode || this.fs.maintenanceMode) && this.updateFirmwares.length === 1) {
          this.downloadSoundbank().then( () => { this.nextTab() });
        }

        break;

      case FlasherSteps.ConfigurationBackup:
        this.actionProgress = '0%';
        this.setProgress('backup', 'wait');
        this.performBackup();
        break;
        
      case FlasherSteps.RpcActivation:
        this.circuitId = undefined;

        this.setProgress('enterRpc', 'wait');  
        this.setProgress('connect', 'wait');  
        break;  

      case FlasherSteps.DeviceConnection:
        break;

      case FlasherSteps.SoundbankFlash:
        this.actionProgress = '0%';
        this.setProgress('flashSoundbank', 'wait');  
        break;

      case FlasherSteps.DeviceReconnect:
        if (this.currentInstrument?.connectionMode === InstrumentConnectionMode.EMEO) {
          this.rebooting = false;
          this.setProgress('rebootDevice',  'done');
          this.setProgress('connectDevice', 'done');
          this.setProgress('configDevice', 'wait');  
        }
        else {
          this.rebooting = true;
          this.setProgress('rebootDevice', 'wait');
          this.setProgress('connectDevice', 'wait');
          this.setProgress('configDevice', 'wait');
        }

        break;
  
      case FlasherSteps.ConfigurationRestore:
        this.actionProgress = '0%';
        this.allowSelection = true;
        this.setProgress('restore', 'wait');
        break;

      case FlasherSteps.Completion:
        this.disconnect().then();
        break;
    }
  }

  finishWizard() {
    this.activeModal.close();
  }

  onSerialPortConnect(event: Event) {
    this.logger.debug("onSerialPortConnect");
    this.isConnected = true;
  }

  onSerialPortDisconnect(event: Event) : any {
    this.logger.debug("onSerialPortDisconnect");
    this.isConnected = false;

    this.device = undefined;
    this.serialPort = undefined;
    this.isInBootloader = false;
  }

  async disconnect() {

    if (this.device) {
      await this.device.reset();
      this.device = undefined;
    }

    if (this.serialPort) {

      let port = this.serialPort
      this.serialPort = undefined;

      port.close()
        .catch( (e) => { log.error(JSON.stringify(e));})
        .finally( () => { this.isConnected = false; });
    }

    this.isInBootloader = false;
  }  

  async connectDevice() {

    this.setProgress('connect', 'active');
      
    if (this.currentInstrument) {

      let connection = this.currentInstrument.connection;

      await this.currentInstrument.switchUSBToRPC();
      await sleep(250);

      this.currentStep = FlasherSteps.SoundbankFlash;

      if (this.directFlash) {
        this.performFlash();
      }            
    }
  }  


  private setProgress(key: string, state: 'active'|'wait'|'done'|'error') {
    this.progress = this.progress || {};
    this.progress[key] = state;
    this.cd.detectChanges();
  }

  private soundbankRange: RPCRange | undefined;
  private rangeInfo: RPCRangeInfo | undefined;

  async enterRpcMode() {

    this.setProgress('enterRpc', 'active');  
    this.setProgress('connect', 'wait');
    
    this.soundbankRange = undefined;
    this.rangeInfo = undefined;

    let rebootWaitMs = 3000;
    if (this.currentInstrument) {
      
      this.logger.debug('Entering RPC mode');
      await this.currentInstrument.switchUSBToRPC();        
      await sleep(rebootWaitMs);
      this.setProgress('enterRpc', 'done');
      
      this.logger.debug('Device should be in RPC mode now.');
      
      this.setProgress('connect', 'active');
      let rpcHandler = new DreamRPC(this.logger, this.observer);
      var connect = await rpcHandler.connect();
      
      if (connect) {
        var rangeInfo = await rpcHandler.requestRanges();

        // We need a range rhat takes the soundbank, otherwise fail.
        for (var range of rangeInfo.ranges) {
          if (range.type == RPCRangeType.SOUNDBANK) {
            this.soundbankRange = range;
            this.rangeInfo = rangeInfo;
            this.rpcHandler = rpcHandler; 
          }
        }

        if (this.soundbankRange) {
          this.setProgress('connect', 'done');
          this.canMoveToNextStep = true;
        }
        else {
          this.setProgress('connect', 'error');
        }
      }
    }
    else {
      this.setProgress('enterRpc', 'error');  
    }
  }

  private rpcHandler : DreamRPC | null = null;

  private firmwareImage?: Uint8Array

  firmwareName?: string = undefined;

  async downloadSoundbank() {

    this.setProgress('loadSoundbank', 'active');
    this.firmwareSelection.disable();

    let version = this.firmwareSelection.value;
    this.firmwareName = version;

    let firmware = this.updateFirmwares.find( (fw: FirmwareInfo) => { return (fw.version == version); } );

    if (firmware) {
      try {
        this.setProgress('loadSoundbank', 'active');
        this.firmwareImage = await this.loadSoundbank(firmware);
        this.canMoveToNextStep = true;
      }
      catch (e) {
        this.setProgress('loadSoundbank', 'error');
      }
    }
    else {
      this.setProgress('loadSoundbank', 'error');
    }

    if (!this.canMoveToNextStep) {
      this.firmwareSelection.enable();
    }
  }

  async resetDevice() {
  }

  async performFlash() {

    if (this.firmwareImage && this.soundbankRange) {

      let success = await this.flashImage(this.firmwareImage, this.soundbankRange);

      this.canMoveToNextStep = true;
      this.nextTab();

      if (success && this.device) {
        try {
          this.setProgress('rebootDevice', 'active');
          this.rebooting = true;
          await this.resetDevice();
        }
        catch (error) {
          this.setProgress('rebootDevice', 'error');
        }
      }
      else {
        this.setProgress('rebootDevice', 'error');
      }

      this.canMoveToNextStep = true;
    }
  }

  async flashImage(data: Uint8Array, range: RPCRange) : Promise<boolean> {

    if (this.rpcHandler) {

      this.setProgress('flashSoundbank', 'active');

      try {
        var result = await this.rpcHandler.programRange(range, data);

        this.setProgress('flashSoundbank', 'done');
        return true;
      }
      catch (error) {
        log.error("Failed soundbank flasher", error);
        this.setProgress('flashSoundbank', 'error');
      }
      finally {
      }
    }

    return false;
  }

  /**
   * Download a firmware image from the server
   * 
   * @param firmware Firmware info 
   */
  async loadSoundbank(firmware: FirmwareInfo) : Promise<Uint8Array | undefined> {

    this.setProgress('loadSoundbank', 'active');
    this.firmwareSelection.disable();

    this.canMoveToNextStep = false;

    let flasher = this;

    let response = await this.firmwareLoadService.getFirmwareImage(firmware.file);

    if (response) {

      this.logger.log('Firmware was successfully downloaded');
      let buffer = await response.arrayBuffer();
      let data = new Uint8Array(buffer);
      this.setProgress('loadSoundbank', 'done');
  
      this.setProgress('verifySoundbank', 'active');
      let checksum = buf(data); 
      let stringChecksum = this.formatChecksum(checksum);
      let requiredChecksum = firmware.checksum.toLowerCase();
  
      if (stringChecksum !== requiredChecksum) {
        this.setProgress('verifySoundbank', 'error');
        this.firmwareSelection.enable();
        throw Error(`Cannot verify firmware. Checksum for ${firmware.file} is ${stringChecksum}, expected checksum is ${requiredChecksum}`);
      }
      else {
        this.setProgress('verifySoundbank', 'done');
        this.canMoveToNextStep = true;
        return data;
      }
    }
    else {
      this.setProgress('loadSoundbank', 'error');
    }

    return undefined;
  }

  private formatChecksum(checksum: number): string {
    return (checksum >>> 0).toString(16).padStart(8, '0').toLowerCase();
  }

  async loadAndflashSoundbank(firmware: FirmwareInfo) {
    let firmwareImage = await this.loadSoundbank(firmware);
    
    if (firmwareImage && this.soundbankRange) {
      await this.flashImage(firmwareImage, this.soundbankRange);
    }
  }

  async connectRpc(connection: InstrumentInterface) {

    this.logger.debug('Attaching Soundbank');

    this.setProgress('enterRpc', 'done');
    this.setProgress('connect', 'done');
    this.setProgress('identify', 'active');
    this.canMoveToNextStep = false;

    let serialPort = connection.port as SerialPort;

    this.cs.disableAutoconnect();

    this.logger.debug('Closing serial connection');
    await connection.close();
    await sleep(500);

    serialPort.onconnect = this.onSerialPortConnect.bind(this);
    serialPort.ondisconnect = this.onSerialPortDisconnect.bind(this);

    /*
    this.logger.debug('Setting up SAM-BA connection');
    this.samba = new SamBA(serialPort, {
      logger: this.logger,
      debug: true
    });
*/

    this.serialPort = serialPort;

    this.isConnected = true;
    this.cs.enableAutoconnect();
    
//    var dev = new Device(theSamba);
//    await dev.create();


    this.setProgress('identify', 'done');
    this.canMoveToNextStep = true;

    this.isInBootloader = true;

    this.currentStep = FlasherSteps.SoundbankFlash;

    if (this.directFlash) {
      this.performFlash();
    }
  }

  async cancelFlasher() {
    let response = await this.confirmService.confirm(
      "Abort Wizard?",
      "Do you really want to abort the firmware update wizard?",
      "Yes", "No"
    );

    if (response) {
      if (this.closeCallback) {
        await this.closeCallback()
      }
    }
  }

  isProgress(key: string, state: string): boolean {
    return (this.progress ? this.progress[ key ] == state : false);
  }

  progressStyles(key: string): any {

    let styles = { 
        'bg-success': this.isProgress(key, 'done'), 
        'bg-danger': this.isProgress(key, 'error'), 
        'bg-primary': this.isProgress(key, 'active'),
        'progress-bar-animated': this.isProgress(key, 'active') 
      }

      return styles;
  }

  @ViewChild('fullscreenElement') fsElement?: ElementRef;

  openFullscreen() {
    this.isFullscreen = true;

    const el = this.fsElement?.nativeElement;
    let doc: any = document;

    if (!doc.fullscreenElement) {  // current working methods
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } 
      else if (el.mozRequestFullScreen) {
        el.mozRequestFullScreen();
      } 
      else if (el.webkitRequestFullscreen) {
        // el.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      } 
      else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
    }

    // setTimeout(() => {
    //   this.isActive = true;
    // }, 500);    
  }

  async connectSerial() {
    log.info("User clicked serial connection");
    let connect = await this.cs.establishSerialConnection()
  }
  
  
  get hasEMEOConnection(): boolean {
    // log.debug("hasEmeoConnection: " + this.hardwareSupport.bluetoothIsSupported);
    return this.cs.hasEMEOConnection;

    return false;
  }

  async uploadSoundbank() {

    this.setProgress('loadSoundbank', 'active');
    this.firmwareSelection.disable();

    try {
      this.setProgress('loadSoundbank', 'active');

      this.firmwareImage = await this.uploadSoundbankFile();
      this.canMoveToNextStep = true;

      this.setProgress('loadSoundbank', 'done');
      this.setProgress('verifySoundbank', 'done');
    }
    catch (e) {
      this.setProgress('loadSoundbank', 'error');
    }

    if (!this.canMoveToNextStep) {
      this.firmwareSelection.enable();
    }
  }

  async uploadSoundbankFile() : Promise<Uint8Array> {

    const pickerOpts = {
      types: [
        {
          description: 'Firmware Images',
          accept: {
            'application/*': ['.bin']
          }
        },
      ],
      excludeAcceptAllOption: true,
      multiple: false
    };

    // open file picker
    let [fileHandle] = await (<any>window).showOpenFilePicker(pickerOpts);

    // get file contents
    if (fileHandle) {
      this.firmwareName = fileHandle.name;
     
      const fileData = await fileHandle.getFile();
      const data = await fileData.arrayBuffer();
  
      return data;
    }

    throw Error("Could not upload firmware image");
  }  

  private _selectedHardware: HardwareInfo | null = null;

  get deviceType() : HardwareInfo {
    return this._selectedHardware || this.currentInstrument?.deviceInfo.hardware || (this.fs.can({ connection: this.cs.emeoConnection }, 'Clarinet') ? NO_DEVICE : EMEO_SAX_V1);
  }

}
